'use client';

import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  DropdownTitle,
  IconButton,
} from '@ads/components-react';
import { ArrowRightStartOnRectangle, Bars3, User } from '@ads/icons';
import { cn } from '@linka/utils';
import { type ComponentType, type ReactNode, useState } from 'react';
import {
  type Language,
  type LanguageOption,
  LanguageSelectorPanel,
  LanguageSelectorTrigger,
} from '../language-selector/language-selector';
import { Skeleton } from '../skeleton/skeleton';

const dropdownItemCustom = { item: '!px-0 hover:!bg-[transparent] data-[highlighted]:!bg-[transparent]' };

/**
 * Workaround: `@ads/components-react` (v1.2.5–3.1.0) compila os tokens
 * `component-*-background-color`/`component-dropdown-border-color` com
 * opacidade 0.00 (fundo do botão secondary e borda do Dropdown somem), e o
 * token de borda do Dropdown (`component-dropdown-border-radius` = 8px,
 * `border-color` = none) não bate com o spec do Figma (16px, `#E5E7EB`,
 * outline de 1px — a config Tailwind do app não define `outlineWidth`, que
 * cai no default do browser de 3px). Remover assim que o Arandu corrigir.
 */
const secondaryButtonBgFix = '!bg-ctx-layout-surface';
const dropdownBorderFix = '!rounded-md !outline !outline-1 !outline-ctx-layout-border';

export interface UserMenuItem {
  key: string;
  label: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  onClick?: () => void;
  href?: string;
}

export interface UserMenuDropdownProps<T extends string = Language> {
  menuAriaLabel: string;
  isPending?: boolean;
  user?: {
    name: string;
  } | null;
  items?: UserMenuItem[];
  signOutLabel: string;
  onSignOut?: () => void;
  accessAccountLabel?: string;
  onAccessAccount?: () => void;
  registerLabel?: string;
  onRegister?: () => void;
  language: T;
  languageOptions?: LanguageOption<T>[];
  languageTitle?: string;
  onLanguageChange: (lang: T) => void;
  /** Notifica abertura/fechamento do menu — usado por consumidores que precisam preparar trabalho sob demanda. */
  onOpenChange?: (open: boolean) => void;
  /** Dispara ao aproximar o ponteiro/foco do trigger — permite pré-aquecer trabalho caro antes do clique. */
  onTriggerWarmUp?: () => void;
  className?: string;
  'data-testid'?: string;
}

export const UserMenuDropdown = <T extends string = Language>({
  menuAriaLabel,
  isPending = false,
  user,
  items,
  signOutLabel,
  onSignOut,
  accessAccountLabel,
  onAccessAccount,
  registerLabel,
  onRegister,
  language,
  languageOptions,
  languageTitle,
  onLanguageChange,
  onOpenChange,
  onTriggerWarmUp,
  className,
  'data-testid': testId,
}: UserMenuDropdownProps<T>) => {
  const [view, setView] = useState<'main' | 'language'>('main');

  if (isPending) return <UserMenuDropdownSkeleton className={className} data-testid={testId} />;

  const isSignedIn = Boolean(user);

  return (
    <Dropdown
      modal
      align='end'
      className={cn('!w-[20rem] !z-[60]', dropdownBorderFix)}
      onOpenChange={(open) => {
        if (!open) setView('main');
        onOpenChange?.(open);
      }}
      trigger={
        // biome-ignore lint/a11y/useAriaPropsSupportedByRole: Dropdown (Radix) clona este <span> como o próprio trigger — role='button' + aria-label dão nome acessível à versão logada (Avatar sem texto visível).
        // biome-ignore lint/a11y/noStaticElementInteractions: mesmo motivo — o Radix transforma este <span> no trigger interativo; onMouseEnter/onFocus apenas pré-aquecem trabalho, sem alterar semântica.
        <span
          className={cn('flex items-center gap-nano', className)}
          data-testid={testId}
          role={user ? 'button' : undefined}
          tabIndex={user ? 0 : undefined}
          aria-label={user ? menuAriaLabel : undefined}
          onMouseEnter={onTriggerWarmUp}
          onFocus={onTriggerWarmUp}
        >
          {user ? (
            <Avatar className='cursor-pointer' fallback={user.name} size='md' delayMS={0} />
          ) : (
            <Button
              type='button'
              hierarchy='tertiary'
              custom={{ root: '!p-nano !rounded-sm !border-hairline border-ctx-layout-border' }}
            >
              <Bars3 aria-hidden />
              <span className='sr-only'>{menuAriaLabel}</span>
            </Button>
          )}
        </span>
      }
    >
      {view === 'language' ? (
        <LanguageSelectorPanel
          language={language}
          options={languageOptions}
          onLanguageChange={(lang) => {
            onLanguageChange(lang);
            setView('main');
          }}
          title={languageTitle}
          onBack={() => setView('main')}
        />
      ) : isSignedIn ? (
        <>
          <div className='flex gap-quark  items-center w-full p-micro '>
            <IconButton className='!rounded-pill' size='sm' icon={User} ariaLabel='' />
            <strong className='ts-subtitle-xxs text-ctx-content-title' data-testid='user-name'>
              {user?.name}
            </strong>
          </div>
          {items?.map((item) => (
            <DropdownItem
              key={item.key}
              custom={{ item: cn('!px-mini', dropdownItemCustom.item) }}
              onClick={item.onClick}
              asChild={Boolean(item.href)}
            >
              {item.href ? (
                <a href={item.href}>
                  <DropdownItemContent leadingIcon={item.icon}>{item.label}</DropdownItemContent>
                </a>
              ) : (
                <DropdownItemContent leadingIcon={item.icon}>{item.label}</DropdownItemContent>
              )}
            </DropdownItem>
          ))}
          <DropdownTitle hasDivider className='p-0'>
            <LanguageSelectorTrigger
              language={language}
              options={languageOptions}
              onClick={() => setView('language')}
            />
          </DropdownTitle>
          <DropdownItem custom={{ item: cn('!px-mini', dropdownItemCustom.item) }}>
            <Button
              type='button'
              hierarchy='tertiary'
              fullWidth
              leadingIcon={ArrowRightStartOnRectangle}
              onClick={onSignOut}
              className='flex items-center !justify-start !ts-paragraph-xxs gap-nano !px-0 !text-ctx-content-base/[0.7] hover:!bg-[transparent] focus:!outline-none focus:!ring-0 focus:!shadow-[none] focus:!bg-[transparent] focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-[none]'
            >
              {signOutLabel}
            </Button>
          </DropdownItem>
        </>
      ) : (
        <>
          {accessAccountLabel && (
            <DropdownItem className='my-micro' custom={dropdownItemCustom} onClick={onAccessAccount}>
              <div className='px-micro w-full'>
                <Button type='button' hierarchy='secondary' fullWidth className={secondaryButtonBgFix}>
                  {accessAccountLabel}
                </Button>
              </div>
            </DropdownItem>
          )}
          {registerLabel && (
            <DropdownItem className='mb-micro' custom={dropdownItemCustom} onClick={onRegister}>
              <div className='px-micro w-full'>
                <Button type='button' hierarchy='tertiary' fullWidth>
                  {registerLabel}
                </Button>
              </div>
            </DropdownItem>
          )}
          <DropdownTitle custom={{ content: cn('!px-mini', dropdownItemCustom.item) }}>
            <LanguageSelectorTrigger
              language={language}
              options={languageOptions}
              onClick={() => setView('language')}
            />
          </DropdownTitle>
        </>
      )}
    </Dropdown>
  );
};

interface UserMenuDropdownSkeletonProps extends Pick<UserMenuDropdownProps, 'className' | 'data-testid'> {}

const UserMenuDropdownSkeleton = ({ className, 'data-testid': testId }: UserMenuDropdownSkeletonProps) => (
  <div className={cn('flex items-center gap-nano', className)} data-testid={testId}>
    <Skeleton className='h-sm w-sm rounded-sm' />
  </div>
);
